import type { TablePaginationConfig } from 'antd';
import { useMemo } from 'react';
import { atom, type RecoilState, type Resetter, useRecoilState, useResetRecoilState } from 'recoil';
import type { TablePaginationShape } from './useTablePagination';

export const filterState = <T>(key: string, defaultValue: T): RecoilState<T> =>
  atom<T>({
    key: `${key}-filterState`,
    default: defaultValue,
  });

export const paginationState = <T extends DefaultPagination>(key: string, defaultValue: T): RecoilState<T> =>
  atom<T>({
    key: `${key}-paginationState`,
    default: defaultValue,
  });

export type DefaultPagination = Required<Pick<TablePaginationConfig, 'pageSize' | 'current' | 'total'>>;

export const DEFAULT_PAGINATION: DefaultPagination = {
  current: 1,
  pageSize: 10,
  total: 0,
};

const useFilterState = <T>(key: string, defaultFilter: T) => {
  const memoizedState = useMemo(() => filterState(key, defaultFilter), [defaultFilter, key]);
  const resetFilterState = useResetRecoilState(memoizedState);
  const [filter, setFilterState] = useRecoilState(memoizedState);

  return {
    filter,
    setFilterState,
    resetFilterState,
  };
};

const usePaginationState = (key: string, defaultPagination?: DefaultPagination) => {
  const memoizedState = useMemo(
    () => paginationState(key, defaultPagination ?? DEFAULT_PAGINATION),
    [defaultPagination, key],
  );
  const resetPaginationState = useResetRecoilState(memoizedState);
  const [pagination, setPaginationState] = useRecoilState(memoizedState);

  return {
    pagination: {
      ...pagination,
      onChange: (newPage: number, newPageSize: number) =>
        setPaginationState(prev => ({ ...prev, pageSize: newPageSize, current: newPage })),
      showSizeChanger: true,
      showTotal: (currentTotal: number, range: [number, number]) =>
        `${range[0]}-${range[1]} of ${currentTotal} ${'Inventory meters'}`,
    },
    setPaginationState,
    resetPaginationState,
  };
};

export interface FilterWithPaginationStateShape<T> {
  filter: T;
  pagination: TablePaginationShape['paginationOptions'];
  resetFilterState: Resetter;
  resetPaginationState: Resetter;
  setFilterState: (update: Partial<T>) => void;
  setPaginationState: (update: Partial<DefaultPagination>) => void;
  setFilterAndPaginationState: (changedFilter: Partial<T>, changedPagination: Partial<DefaultPagination>) => void;
}

/**
 * @deprecated Use `useFilterWithPaginationV2` instead.
 */
export const useFilterWithPagination = <T>(
  stateKey: string,
  defaultFilter: T,
  defaultPagination?: DefaultPagination,
): FilterWithPaginationStateShape<T> => {
  const { pagination, setPaginationState, resetPaginationState } = usePaginationState(stateKey, defaultPagination);
  const { filter, setFilterState, resetFilterState } = useFilterState(stateKey, defaultFilter);

  const handleSetState = (changedFilter: Partial<T>, changedPagination: Partial<DefaultPagination>) => {
    setFilterState(prevState => ({ ...prevState, ...changedFilter }));
    setPaginationState(prevState => ({ ...prevState, ...changedPagination }));
  };

  return {
    filter,
    pagination,
    resetFilterState,
    resetPaginationState,
    setFilterState: update => setFilterState(prevState => ({ ...prevState, ...update })),
    setPaginationState: update => setPaginationState(prevState => ({ ...prevState, ...update })),
    setFilterAndPaginationState: handleSetState,
  };
};
